import { IKeyObjective } from 'legacy/lut/classes/models/partner360/IFormOptionsJBP'
import { IKIBusinessPriority } from 'legacy/lut/features/partner360/jbpSettings/models/kiBusinessPriority'

import { getQualityBand, TQualityBand } from 'partner360/components/qualityIndicator'
import {
  getActionableDescriptionQualityReasons,
  parseDescriptionQuality,
  TKeyInitiative,
} from 'partner360/entities/keyInitiatives'
import { isMdfStrategicInitiative } from 'partner360/features/keyInitiativeEditor/lib/keyInitiativeMdfFunding'
import {
  getPlanField,
  getPrevYearActualsField,
} from 'partner360/features/keyInitiatives/lib/financialFieldHelper'
import { TKeyInitiativesFinancialData } from 'partner360/features/keyInitiatives/model/financialData'
import {
  getGoalField,
  getInitiativeIncrementalField,
} from 'partner360/features/keyInitiatives/ui/helpers/fieldHelper'
import { getNumberValue } from 'partner360/features/keyInitiatives/ui/helpers/getNumberValue'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { IDescriptionQualityGapInitiative, IFinancialTargetGapInitiative } from '../models'

import { isLowDescriptionQuality } from './isLowDescriptionQuality'

interface IGetInitiativesPlanQualityParams {
  data: TKeyInitiative[]
  finData: TKeyInitiativesFinancialData[]
  businessPriorities: IKIBusinessPriority[]
  keyObjectives?: readonly IKeyObjective[]
  activeCurrency: string
  actualsType: ActualsValueTypes
}

const normalizeUid = (uid: string) => uid.trim().toLowerCase()

const parseBusinessPriorityUids = (value?: string | null): string[] =>
  value?.split(',').map(normalizeUid).filter(Boolean) ?? []

const capScore = (value: number) => Math.min(Math.max(value, 0), 100)

const getAverage = (values: number[]) => {
  if (!values.length) {
    return 0
  }

  return values.reduce((sum, value) => sum + value, 0) / values.length
}

const getRoundedAverage = (values: number[]) => Math.round(getAverage(values))

const getCoveragePercent = (value: number, target: number) => {
  if (!target) {
    return 0
  }

  return (value / target) * 100
}

function getQualityResult(score: number): { score: number; band: TQualityBand }
function getQualityResult(
  score: number | null
): { score: number; band: TQualityBand } | { score: null; band: null }
function getQualityResult(score: number | null) {
  if (score === null) {
    return { score: null, band: null }
  }

  return {
    score,
    band: getQualityBand(score),
  }
}

export const getInitiativesPlanQuality = ({
  data,
  finData,
  businessPriorities,
  keyObjectives = [],
  activeCurrency,
  actualsType,
}: IGetInitiativesPlanQualityParams) => {
  const goalField = getGoalField(actualsType, activeCurrency)
  const incrementalField = getInitiativeIncrementalField(actualsType, activeCurrency)
  const planField = getPlanField(actualsType, activeCurrency)
  const prevYearActualsField = getPrevYearActualsField(actualsType, activeCurrency)

  const finItem = finData[0]

  const availablePriorityUids = new Set(businessPriorities.map(({ uid }) => normalizeUid(uid)))

  const enrichedInitiatives = data.map((item) => {
    const goal = getNumberValue(item, goalField)
    const incremental = getNumberValue(item, incrementalField)
    const priorityUids = parseBusinessPriorityUids(item.BusinessPriorities)
    const descriptionQuality = parseDescriptionQuality(item.DescriptionQuality)
    const descriptionScore = descriptionQuality?.Score ?? null
    const reasons = getActionableDescriptionQualityReasons(descriptionQuality?.Reasons ?? [])
    const isLowScored = isLowDescriptionQuality(descriptionScore, reasons)

    return {
      item,
      goal,
      incremental,
      priorityUids,
      descriptionScore,
      reasons,
      hasFinancialTarget: incremental > 0,
      isLowScored,
      isWellDescribed: !isLowScored,
    }
  })

  const totalGoal = enrichedInitiatives.reduce((sum, { goal }) => sum + goal, 0)

  const totalIncremental = enrichedInitiatives.reduce(
    (sum, { incremental }) => sum + incremental,
    0
  )

  const planValue = getNumberValue(finItem, planField)
  const prevYearActualsValue = getNumberValue(finItem, prevYearActualsField)

  const goalTarget = planValue
  const incrementalTarget = planValue - prevYearActualsValue

  const goalCoverage = getCoveragePercent(totalGoal, goalTarget)
  const incrementalCoverage = capScore(getCoveragePercent(totalIncremental, incrementalTarget))

  // const financialPlanCoverage = getRoundedAverage([
  //     capScore(goalCoverage),
  //     capScore(incrementalCoverage),
  //   ])
  const financialPlanCoverage = incrementalTarget < 0 ? null : Math.round(incrementalCoverage)

  const descriptionScores = enrichedInitiatives
    .map(({ descriptionScore }) => descriptionScore)
    .filter((score): score is number => score !== null)

  const descriptionQuality = getRoundedAverage(descriptionScores)

  const coveredPriorityUids = new Set(
    enrichedInitiatives
      .flatMap(({ priorityUids }) => priorityUids)
      .filter((uid) => availablePriorityUids.has(uid))
  )

  const initiativesWithBusinessPriorities = enrichedInitiatives.filter(
    ({ priorityUids }) => priorityUids.length > 0
  )

  const mdfInitiatives = enrichedInitiatives.filter(({ item }) =>
    isMdfStrategicInitiative(
      item.Development,
      keyObjectives.filter(({ fYear }) => fYear === item.FYear)
    )
  )

  const strategicGrowthAreasScore = capScore(
    (mdfInitiatives.length + initiativesWithBusinessPriorities.length) * 50
  )

  const priorityCoverage = businessPriorities.map((priority) => {
    const priorityUid = normalizeUid(priority.uid)

    return {
      priority,
      initiatives: enrichedInitiatives
        .filter(({ priorityUids }) => priorityUids.includes(priorityUid))
        .map(({ item }) => item),
    }
  })

  const initiativesWithoutFinancialTargets: IFinancialTargetGapInitiative[] = enrichedInitiatives
    .filter(({ hasFinancialTarget }) => !hasFinancialTarget)
    .map(({ item, goal, incremental }) => ({
      EntryId: item.EntryId,
      Name: item.Name,
      goal,
      incremental,
      initiative: item,
    }))

  const initiativesWithIncremental: IFinancialTargetGapInitiative[] = enrichedInitiatives
    .filter(({ incremental }) => incremental > 0)
    .map(({ item, goal, incremental }) => ({
      EntryId: item.EntryId,
      Name: item.Name,
      goal,
      incremental,
      initiative: item,
    }))

  const lowScoredInitiatives: IDescriptionQualityGapInitiative[] = enrichedInitiatives
    .filter(({ isLowScored }) => isLowScored)
    .map(({ item, reasons }) => ({
      EntryId: item.EntryId,
      Name: item.Name,
      Description: item.Description,
      reasons,
      initiative: item,
    }))

  const wellDescribedCount = enrichedInitiatives.filter(
    ({ isWellDescribed }) => isWellDescribed
  ).length

  const overall = getRoundedAverage(
    [financialPlanCoverage, descriptionQuality, strategicGrowthAreasScore].filter(
      (score): score is number => score !== null
    )
  )

  return {
    overall: getQualityResult(overall),

    financialPlanCoverage: {
      ...getQualityResult(financialPlanCoverage),
      goalCoverage: Math.round(goalCoverage),
      incrementalCoverage: Math.round(incrementalCoverage),
      incrementalPlanSum: Math.round(incrementalTarget),
      incrementalInitiativesSum: Math.round(totalIncremental),
      allInitiativesHaveTargetsSet: initiativesWithoutFinancialTargets.length === 0,
      initiativesWithoutFinancialTargets,
      initiativesWithIncremental,
    },

    descriptionQuality: {
      ...getQualityResult(descriptionQuality),
      wellDescribedCount,
      totalCount: data.length,
      lowScoredCount: lowScoredInitiatives.length,
      lowScoredInitiatives,
    },

    strategicGrowthAreas: {
      ...getQualityResult(strategicGrowthAreasScore),
      band: (strategicGrowthAreasScore === 0
        ? 'Low'
        : strategicGrowthAreasScore === 50
          ? 'Moderate'
          : 'High') as TQualityBand,
      mdfInitiatives: mdfInitiatives.map(({ item }) => item),
      initiativesWithBusinessPrioritiesCount: initiativesWithBusinessPriorities.length,
      initiativesWithoutBusinessPriorities: enrichedInitiatives
        .filter(({ priorityUids }) => priorityUids.length === 0)
        .map(({ item }) => item),
      coveredCount: coveredPriorityUids.size,
      totalCount: businessPriorities.length,
      priorityCoverage,
    },
  }
}
